import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";
import SEORelatedPages from "@/components/SEORelatedPages";
import { getSEOData } from "@/lib/seo-pages";

const seo = getSEOData("/pesnya-rebenku-v-podarok")!;

const faqItems = [
  { q: "С какого возраста можно подарить ребёнку песню?", a: "С любого. Малышам — колыбельную. Школьникам — весёлый трек. Подросткам — рэп или поп." },
  { q: "Что рассказать про ребёнка?", a: "Имя, любимые игры, смешные привычки, мечты, друзей, питомцев." },
  { q: "Можно ли сделать песню от имени Деда Мороза?", a: "Конечно. Дети в восторге, когда слышат своё имя в песне от любимого героя." },
  { q: "Подойдёт ли песня как подарок на день рождения?", a: "Идеально. Можно включить на празднике при друзьях." },
  { q: "Сколько стоит детская персональная песня?", a: "Демоверсия бесплатна. Полная версия — от 249 ₽." },
];

const PesnyaRebenkuVPodarok = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={seo.title} description={seo.description} canonical={seo.canonical} jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqItems.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })) }} />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container px-4 mx-auto max-w-3xl">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">Песня ребёнку в подарок</h1>

          <div className="space-y-4 text-muted-foreground leading-relaxed mb-10">
            <p>Вся эта история — от роддома до сегодняшнего дня — может стать <strong className="text-foreground">песней</strong>. Настоящей, с его именем в каждом куплете.</p>
            <p><strong className="text-foreground">Персональная песня ребёнку в подарок</strong> — это признание в любви от родителей, бабушек, дедушек, крёстных.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Почему дети обожают песни про себя</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Когда из колонки звучит <strong className="text-foreground">песня с их именем</strong> — они замирают. «Это правда про меня?!»</p>
              <p>Это песня, которую ребёнок будет слушать, когда вырастет. И помнить, что его любили.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">✨ Создать песню для ребёнка</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Демоверсия бесплатна. Готово за 3 минуты.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">На какой праздник подарить ребёнку песню</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p><strong className="text-foreground"><Link to="/pesnya-na-den-rozhdeniya" className="text-primary hover:underline">День рождения</Link>.</strong> Именинник станет звездой вечера.</p>
              <p><strong className="text-foreground">Новый год.</strong> Песня от Деда Мороза, который знает про двойку по математике. Волшебство!</p>
              <p><strong className="text-foreground"><Link to="/pesnya-na-vypusknoj" className="text-primary hover:underline">Выпускной</Link> из детского сада или школы.</strong></p>
              <p><strong className="text-foreground">Просто так.</strong> Иногда лучший повод — отсутствие повода.</p>
            </div>
          </section>

          <section className="mb-12">
            <h3 className="font-display text-lg sm:text-xl font-bold mb-3 text-foreground">«Дочка слушает свою песню каждое утро. Уже три месяца подряд»</h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Мама шестилетней Алисы заказала песню на день рождения. Алиса танцевала по комнате, крича: «Мама, это песня ПРО МЕНЯ!» С тех пор слушает каждое утро за завтраком.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎵 Подарить ребёнку волшебство в музыке</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Без регистрации. Без приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-foreground">Частые вопросы о песнях для детей</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card">
                  <AccordionTrigger className="text-left hover:no-underline text-sm font-medium text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎶 Пусть детство зазвучит</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Создайте песню, которую ваш ребёнок будет помнить, когда вырастет.</p>
          </div>

          <SEORelatedPages pages={seo.relatedPages || []} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PesnyaRebenkuVPodarok;
